import { FC } from "react";
import Button from "components/Button";
import {
  ModalContent,
  ModalSections,
  ModalButtonsSection,
} from "components/Modal";
import { InformationCircleIcon } from "@heroicons/react/outline";
import Select from "components/Select";
import { capitalize } from "lodash";
import { FilterLabel } from "./shared";

export type ErrorFilterSelection = {
  domain: string;
  statusCode: string;
  severity: string;
};

const ErrorFiltersModalContent: FC<{
  domains: string[];
  statusCodes: string[];
  severities: readonly string[];
  selected: ErrorFilterSelection;
  onReset: () => void;
  onChange: (updates: Partial<ErrorFilterSelection>) => void;
  onCancel: () => void;
  onApply: () => void;
}> = ({
  domains,
  statusCodes,
  severities,
  selected,
  onReset,
  onChange,
  onCancel,
  onApply,
}) => {
  const domainOptions = ["All", ...domains];
  const statusCodeOptions = ["All", ...statusCodes.map(String)];
  const severityOptions = ["All", ...severities.map(capitalize)];

  return (
    <ModalContent smallerGapFromButtons>
      <ModalSections>
        <div className="flex flex-col gap-6 text-brandDarkest relative">
          {/* Domains Filter */}
          <div>
            <FilterLabel text="Domains" tooltip="Filter by specific domains where errors occurred" icon={InformationCircleIcon} className="mb-2 text-sm font-medium text-gray-700" />
            <Select
              value={selected.domain || "All"}
              options={domainOptions}
              onChange={(value) =>
                onChange({ domain: value === "All" ? "" : value })
              }
              size="SM"
              className="w-full"
              buttonClassName="w-full"
            />
          </div>

          {/* Status Code Filter */}
          <div>
            <FilterLabel text="Status code" tooltip="Filter by HTTP status codes (400, 500, etc.)" icon={InformationCircleIcon} className="mb-2 text-sm font-medium text-gray-700" />
            <Select
              value={selected.statusCode || "All"}
              options={statusCodeOptions}
              onChange={(value) =>
                onChange({ statusCode: value === "All" ? "" : value })
              }
              size="SM"
              className="w-full"
              buttonClassName="w-full"
            />
          </div>

          {/* Severity Filter */}
          <div>
            <FilterLabel text="Severity" tooltip="Filter by error severity level (Low, Medium, High)" icon={InformationCircleIcon} className="mb-2 text-sm font-medium text-gray-700" />
            <Select
              value={selected.severity ? capitalize(selected.severity) : "All"}
              options={severityOptions}
              onChange={(value) =>
                onChange({
                  severity: value === "All" ? "" : value.toLowerCase(),
                })
              }
              size="SM"
              className="w-full"
              buttonClassName="w-full"
            />
          </div>
        </div>
      </ModalSections>

      <ModalButtonsSection justifyBetween="justify-between">
        <Button
          text="Reset Filters"
          className="button button-secondary"
          onClick={onReset}
        />
        <div className="flex gap-3">
          <Button
            text="Cancel"
            className="button button-tertiary"
            onClick={onCancel}
            size="MD"
          />
          <Button
            text="Apply Filters"
            className="button button-primary"
            onClick={onApply}
            size="MD"
          />
        </div>
      </ModalButtonsSection>
    </ModalContent>
  );
};

export default ErrorFiltersModalContent;
